"use client"

import { ENTERPRISE_INQUIRY_RECIPIENT } from "@/lib/email/sales-inbox"
import type { NotificationChannelType } from "@/lib/notifications/types"
import { NotificationGroup } from "./notification-group"
import { NotificationRow } from "./notification-row"
import { useTranslations } from "next-intl"



interface OtherMessage {
  label: string
  description: string
  channelType: NotificationChannelType
  from: string
}

interface OtherMessageGroup {
  id: string
  title: string
  audience: string
  messages: readonly OtherMessage[]
}

const TENANT_MAILBOX = "The tenant's connected Gmail, else the platform mailbox."

/** Sent outside the notification catalog; a new message of this kind belongs here until it joins the catalog. */
function getOTHER_MESSAGE_GROUPS(tUi: ReturnType<typeof useTranslations<"SystemNotifications.SystemNotificationsOtherMessagesTab">>): readonly OtherMessageGroup[] {
  return [
  {
    id: "account",
    title: tUi("accountEmails"),
    audience: tUi("sentToThePersonTheActionConcerns"),
    messages: [
      {
        label: tUi("emailVerification"),
        description:
          tUi("confirmsANewAccountSEmailAfterSign"),
        channelType: "email",
        from: tUi("thePlatformMailboxARepeatedOneComesFrom"),
      },
      {
        label: tUi("passwordReset"),
        description: tUi("someoneAskedToResetAnAccountSPassword"),
        channelType: "email",
        from: TENANT_MAILBOX,
      },
      {
        label: tUi("invitation"),
        description: tUi("anAdminInvitedAUserToATenant"),
        channelType: "email",
        from: TENANT_MAILBOX,
      },
      {
        label: tUi("invitationReminder"),
        description: tUi("anAdminResentAPendingInvitation"),
        channelType: "email",
        from: TENANT_MAILBOX,
      },
    ],
  },
  {
    id: "operators",
    title: tUi("operatorEmails"),
    audience: tUi("sentToEveryActiveSuperUserAndIntegrator"),
    messages: [
      {
        label: tUi("planExpirySummary"),
        description: tUi("theDailyPlanExpiryRunDeactivatedOneOr"),
        channelType: "email",
        from: tUi("thePlatformMailbox"),
      },
    ],
  },
  {
    id: "sales",
    title: tUi("salesEmails"),
    audience: tUi("sentToTheSalesInboxValue0", { value0: String(ENTERPRISE_INQUIRY_RECIPIENT) }),
    messages: [
      {
        label: tUi("enterpriseInquiry"),
        description: tUi("someoneAskedAboutTheEnterprisePlanThroughContact"),
        channelType: "email",
        from: tUi("thePlatformMailbox"),
      },
    ],
  },
  {
    id: "telegram",
    title: tUi("telegramBotReplies"),
    audience: tUi("sentInTheChatThatWroteToA"),
    messages: [
      {
        label: tUi("chatNotSubscribed"),
        description: tUi("someoneSentStartToANotificationBotWithout"),
        channelType: "telegram",
        from: tUi("theChannelSNotificationBot"),
      },
    ],
  },
]
}

export function OtherMessagesTab() {

  const tUi = useTranslations("SystemNotifications.SystemNotificationsOtherMessagesTab")
  const OTHER_MESSAGE_GROUPS = getOTHER_MESSAGE_GROUPS(tUi)


  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        <span key={tUi("messagesThePlatformSendsOutsideTheNotificationCatalog")}>{tUi("messagesThePlatformSendsOutsideTheNotificationCatalog")}</span></p>
      {OTHER_MESSAGE_GROUPS.map((group) => (
        <NotificationGroup key={group.id} id={`other-group-${group.id}`} title={group.title} description={group.audience}>
          {group.messages.map((message) => (
            <NotificationRow
              key={message.label}
              label={message.label}
              description={message.description}
              channelTypes={[message.channelType]}
            >
              <p className="text-xs text-muted-foreground">{<span key={tUi("fromValue0", { value0: String(message.from) })}>{tUi("fromValue0", { value0: String(message.from) })}</span>}</p>
            </NotificationRow>
          ))}
        </NotificationGroup>
      ))}
    </div>
  )
}
